import type { ReactNode } from 'react';

/**
 * Barra de filtros de una tabla del panel.
 *
 * Existe para que las pestañas filtren TODAS igual: mismos controles, mismo orden, mismo
 * "Limpiar filtros". Cuando cada pantalla se armaba su propia barra, "Asignadas" tenía tres
 * filtros y las otras ninguno, y ninguna decía cuántas filas estaba escondiendo.
 *
 * Los controles van en el orden en que la gente los usa: primero buscar a la persona, después
 * acotar fechas, y al final los que casi nunca se tocan. En celular envuelven solos.
 */
export default function BarraFiltros({ children, onLimpiar, mostrarLimpiar }: {
  children: ReactNode;
  onLimpiar: () => void;
  /** Solo aparece cuando hay algo puesto: un botón que no hace nada estorba. */
  mostrarLimpiar: boolean;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      {children}
      {mostrarLimpiar && (
        <button onClick={onLimpiar} className="min-h-[44px] rounded border px-3 py-2">
          Limpiar filtros
        </button>
      )}
    </div>
  );
}

const ETIQUETA = 'text-sm text-gray-600';
const CONTROL = 'mt-1 block w-full rounded border px-3 py-2';

export function FiltroFecha({ etiqueta, valor, onChange }: {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block min-w-[9rem] flex-1 lg:flex-none">
      <span className={ETIQUETA}>{etiqueta}</span>
      <input
        type="date"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className={CONTROL}
      />
    </label>
  );
}

export interface OpcionFiltro {
  valor: string;
  etiqueta: string;
}

export function FiltroSelect({ etiqueta, valor, onChange, opciones, todas }: {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  opciones: OpcionFiltro[];
  /** Texto de la opción que no filtra ("Todas las tiendas", "Todos los estados"…). */
  todas: string;
}) {
  return (
    <label className="block min-w-[9rem] flex-1 lg:flex-none">
      <span className={ETIQUETA}>{etiqueta}</span>
      <select value={valor} onChange={(e) => onChange(e.target.value)} className={CONTROL}>
        <option value="">{todas}</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
        ))}
      </select>
    </label>
  );
}

export function FiltroTexto({ etiqueta, valor, onChange, placeholder }: {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block min-w-[12rem] flex-1 lg:flex-none">
      <span className={ETIQUETA}>{etiqueta}</span>
      <input
        type="search"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={CONTROL}
      />
    </label>
  );
}
